import React, { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useIntl } from 'ha/i18n';
import { SearchAreaFilterValue, SearchAreaValue } from 'ha/types/SearchFilters';
import { track } from '@ha/analytics';
import { useSelector } from 'react-redux';
import { getMapViewState } from 'ha/pages/Search/selectors/mapSelectors';
import { MapViewState } from 'ha/pages/Search/utils';

import { CommonCheckboxFilter } from './CommonCheckboxFilter';

export interface Props {
  value: SearchAreaFilterValue;
  onChange: (value: SearchAreaFilterValue) => void;
  checkboxSize?: 'small' | 'medium';
  location: 'filter' | 'map' | 'search results';
}

const SearchAreaFilter: React.FC<Props> = ({
  value,
  onChange,
  checkboxSize = 'medium',
  location,
}) => {
  const { T } = useIntl();

  const initialized = useRef(false);
  const mapViewState = useSelector(getMapViewState);
  const shouldSetMap = useRef(false);
  const [searchParams] = useSearchParams();
  const hasCoords = Boolean(
    searchParams.get('lLng') &&
      searchParams.get('rLng') &&
      searchParams.get('tLat') &&
      searchParams.get('bLat'),
  );

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      if (mapViewState === MapViewState.noMap && !hasCoords) {
        shouldSetMap.current = true;
      }
    }
  }, []);

  useEffect(() => {
    if (shouldSetMap.current && mapViewState === MapViewState.fullMap) {
      shouldSetMap.current = false;
      if (value.includes(SearchAreaValue.MapBounds)) {
        onChange([SearchAreaValue.CityBounds]);
      }
    }
  }, [mapViewState]);

  const onSelect = React.useCallback(
    (selectedValue: SearchAreaFilterValue) => {
      const enabled = selectedValue.includes(SearchAreaValue.MapBounds);
      onChange(
        enabled ? [SearchAreaValue.MapBounds] : [SearchAreaValue.CityBounds],
      );

      track('Map as filter clicked', {
        location,
        state: enabled ? 'enabled' : 'disabled',
      });
    },
    [location, onChange],
  );

  return (
    <CommonCheckboxFilter
      value={value}
      onChange={onSelect}
      checkboxSize={checkboxSize}
      options={[
        {
          value: SearchAreaValue.MapBounds,
          title: T('search.map.map_as_filter.checkbox'),
          testLocator: 'Filter Use Map As Search Area',
        },
      ]}
    />
  );
};

export { SearchAreaFilter };
